import { useCallback, useRef } from 'react';

/**
 * Wrap an async handler function to prevent concurrent calls.
 *
 * Only one invocation of the handler will be allowed at a time. If the handler
 * is invoked while another invocation is in progress, the second invocation will
 * be ignored.
 *
 * Usage:
 * ```js
 * const handleEvent = useSafeHandler(useCallback(async () => {
 *   // handler code here
 * }, [
 *   // handler dependencies here
 * ]))
 * ```
 */
export function useSafeHandler<Args extends unknown[], R>(
  handler: (...args: Args) => Promise<R>
) {
  const isRunning = useRef(false);

  return useCallback(
    async (...args: Parameters<typeof handler>) => {
      if (isRunning.current) return;
      isRunning.current = true;
      return (async () => await handler(...args))().finally(() => {
        isRunning.current = false;
      });
    },
    [handler]
  );
}
